import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  Link,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import OrbitBackdrop from '@/components/OrbitBackdrop';
import BrandLogo from '@/components/BrandLogo';
import { authApi } from '@/api/auth';
import { billingApi } from '@/api/billing';
import { apiError } from '@/api/client';
import { useStripeCardElement } from '@/hooks/useStripeCardElement';
import { useAuthStore } from '@/store/authStore';
import { getSelectedPackage, clearSelectedPackage, setSelectedPackage } from '@/utils/selectedPackage';
import { PALETTE } from '@/theme/tokens';

function priceParts(prices) {
  const price = prices?.[0];
  if (!price) return null;
  try {
    return {
      amount: new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: price.currency,
        maximumFractionDigits: Number(price.amount) % 1 === 0 ? 0 : 2,
      }).format(price.amount),
      suffix: '/mo',
    };
  } catch {
    return { amount: `${price.amount} ${price.currency}`, suffix: '/mo' };
  }
}

function limitLine(value, one, many, unlimited) {
  if (value === null || value === undefined) return unlimited;
  return `${value} ${value === 1 ? one : many}`;
}

/**
 * Pricing popup — shown right after a new account signs in (email confirmed,
 * or Google / Facebook), while `user.needs_payment` is true.
 *
 * Rendered by ProtectedRoute over a branded backdrop for ANY protected URL
 * (`gate`), and by the /subscribe route for old links and the 402 redirect in
 * api/client.js. It lists the packages managed in WeManage -> Billing ->
 * Packages; every plan starts with a free trial (`saas.trial_days`, 14). The
 * card is turned into a Stripe PaymentMethod in the browser and
 * POST /billing/start-trial verifies it (small charge, refunded at once) and
 * opens the trial; Stripe bills the plan when the trial ends.
 *
 * The server is the control: until this is done every app API answers 402
 * payment_required (EnsureSubscribed). This popup is the way through it.
 *
 * @param {boolean} gate  true when rendered by ProtectedRoute in place of the page.
 */
export default function Subscribe({ gate = false }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const logout = useAuthStore((s) => s.logout);
  const [error, setError] = useState('');

  /*
   * Ask the server, not the cached user, whether payment is still needed — the
   * stored user can be stale (another tab already paid, or it was cached before
   * this flow existed).
   */
  const { data: me, isLoading: meLoading } = useQuery({
    queryKey: ['subscribe-me'],
    queryFn: () => authApi.me(),
    staleTime: 0,
    retry: false,
  });
  useEffect(() => {
    if (me?.user) setUser(me.user);
  }, [me, setUser]);

  const { data: config, isLoading: configLoading } = useQuery({
    queryKey: ['signup-config'],
    queryFn: () => authApi.signupConfig(),
    staleTime: 60_000,
  });

  // Only plans that can be bought here: priced, not "contact sales".
  const packages = useMemo(
    () => (config?.packages || []).filter((p) => !p.is_custom && p.prices?.length),
    [config],
  );
  const trialDays = config?.trial_days || 14;

  const [slug, setSlug] = useState(() => getSelectedPackage() || user?.selected_package_slug || '');

  // Snap to a real plan once the catalog loads: website choice, then the
  // account's saved choice, then the default package, then the featured one.
  useEffect(() => {
    if (!packages.length || packages.some((p) => p.slug === slug)) return;
    const fallback =
      packages.find((p) => p.slug === me?.user?.selected_package_slug) ||
      packages.find((p) => p.slug === config?.default_package_slug) ||
      packages.find((p) => p.is_featured) ||
      packages[0];
    setSlug(fallback.slug);
  }, [packages, slug, config, me]);

  const chosen = packages.find((p) => p.slug === slug);
  const chosenPrice = priceParts(chosen?.prices);

  const loading = meLoading || configLoading;
  const stripeKey = config?.require_card_for_signup ? config?.stripe_publishable_key : null;

  /*
   * The card field lives inside a Dialog, which portals its content and
   * mounts it a render later than this component. useStripeCardElement mounts
   * Stripe once, when its key changes, and gives up if the container isn't
   * there yet — so the key is only handed over once the container node
   * actually exists (tracked through a callback ref).
   */
  const [cardHost, setCardHost] = useState(null);
  const { containerRef, ready, cardError, loadError, createPaymentMethod } = useStripeCardElement(
    cardHost && !loading ? stripeKey : null,
  );
  const cardHostRef = useCallback(
    (el) => {
      containerRef.current = el;
      setCardHost(el);
    },
    [containerRef],
  );

  const goNext = (fresh) => {
    const hasBusiness = Number(fresh?.businesses_count || 0) > 0;
    navigate(hasBusiness ? '/dashboard' : '/onboarding', { replace: true });
  };

  const refreshUser = async (fallback) => {
    try {
      const fresh = (await authApi.me())?.user || fallback;
      if (fresh) setUser(fresh);
      return fresh;
    } catch {
      if (fallback) setUser(fallback);
      return fallback;
    }
  };

  const mutation = useMutation({
    mutationFn: (payload) => billingApi.startTrial(payload),
    onSuccess: async (data) => {
      clearSelectedPackage();
      goNext(await refreshUser(data?.user));
    },
    onError: async (e) => {
      // Already subscribed (second tab, double submit): refresh and move on.
      if (e?.response?.data?.code === 'already_subscribed') {
        goNext(await refreshUser(null));
        return;
      }
      setError(apiError(e, 'We could not start your free trial. Please try again.'));
    },
  });

  const [creatingPm, setCreatingPm] = useState(false);
  const busy = mutation.isPending || creatingPm;

  const onSubmit = async (event) => {
    event.preventDefault();
    if (busy || !chosen) return;
    setError('');

    let paymentMethodId;
    setCreatingPm(true);
    try {
      paymentMethodId = await createPaymentMethod({ name: user?.name, email: user?.email });
    } catch (e) {
      setError(e.message || 'Could not verify your card details.');
      return;
    } finally {
      setCreatingPm(false);
    }

    mutation.mutate({ package_slug: chosen.slug, payment_method_id: paymentMethodId });
  };

  const signOut = async () => {
    try {
      await authApi.logout();
    } catch {
      /* signing out locally is what matters here */
    }
    logout();
    navigate('/login', { replace: true });
  };

  // Nothing to pay for (already subscribed, or card-required signup is off).
  // As the gate, the store update above re-renders ProtectedRoute into the
  // page that was asked for; as the /subscribe page, go to the dashboard.
  if (me?.user && !me.user.needs_payment) {
    return gate ? null : <Navigate to="/dashboard" replace />;
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        justifyContent: 'center',
        pt: 5,
        background: 'linear-gradient(180deg, #EDEFF6 0%, #E8EAF4 55%, #EFF0F7 100%)',
      }}
    >
      <OrbitBackdrop />
      <Box sx={{ position: 'relative', zIndex: 1 }}>
        <BrandLogo height={40} />
      </Box>

      <Dialog
        open
        fullWidth
        maxWidth="md"
        fullScreen={fullScreen}
        disableEscapeKeyDown
        // Not dismissable: until a plan is chosen there is nothing behind it to
        // go back to (the server answers app APIs with 402).
        onClose={() => {}}
        aria-labelledby="pricing-dialog-title"
        PaperProps={{ sx: { borderRadius: fullScreen ? 0 : 4 } }}
        slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(30,42,94,.35)', backdropFilter: 'blur(3px)' } } }}
      >
        <DialogContent sx={{ p: { xs: 2.5, sm: 4 } }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <Box component="form" onSubmit={onSubmit} noValidate>
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Chip
                  label={`${trialDays} days free on every plan`}
                  size="small"
                  sx={{ mb: 1.5, fontWeight: 700, bgcolor: PALETTE.primarySoft, color: 'primary.main' }}
                />
                <Typography id="pricing-dialog-title" variant="h5" sx={{ fontWeight: 800, letterSpacing: '-.01em' }}>
                  Choose your plan
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                  Try everything free for {trialDays} days. Cancel any time before the trial ends
                  and you won&apos;t be charged.
                </Typography>
              </Box>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {!packages.length && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  No plans are available right now. Please contact support.
                </Alert>
              )}

              <Box
                role="radiogroup"
                aria-label="Plans"
                sx={{
                  display: 'grid',
                  gap: 1.5,
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: packages.length > 1 ? 'repeat(2, 1fr)' : '1fr',
                    md: `repeat(${Math.min(Math.max(packages.length, 1), 3)}, 1fr)`,
                  },
                  mb: 3,
                }}
              >
                {packages.map((pkg) => {
                  const selected = pkg.slug === slug;
                  const price = priceParts(pkg.prices);
                  return (
                    <Box
                      key={pkg.slug}
                      component="button"
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      disabled={busy}
                      onClick={() => {
                        setSlug(pkg.slug);
                        setSelectedPackage(pkg.slug);
                      }}
                      sx={{
                        textAlign: 'left',
                        font: 'inherit',
                        cursor: busy ? 'default' : 'pointer',
                        position: 'relative',
                        p: 2.25,
                        borderRadius: 3,
                        border: '2px solid',
                        borderColor: selected ? 'primary.main' : PALETTE.divider,
                        bgcolor: selected ? PALETTE.primarySoft : 'background.paper',
                        transition: 'border-color .15s ease, background-color .15s ease',
                        '&:hover': { borderColor: selected ? 'primary.main' : '#C9CFF0' },
                        '&:focus-visible': { outline: '3px solid rgba(61,90,254,.35)', outlineOffset: 2 },
                      }}
                    >
                      {pkg.is_featured && (
                        <Chip
                          label="Most popular"
                          size="small"
                          color="primary"
                          sx={{ position: 'absolute', top: 12, right: 12, height: 22, fontWeight: 700, fontSize: 11 }}
                        />
                      )}
                      <Typography sx={{ fontWeight: 800, fontSize: 16, pr: pkg.is_featured ? 10 : 0 }}>
                        {pkg.name}
                      </Typography>
                      {price && (
                        <Typography sx={{ mt: 0.75 }}>
                          <Box component="span" sx={{ fontWeight: 800, fontSize: 26, letterSpacing: '-.02em' }}>
                            {price.amount}
                          </Box>
                          <Box component="span" sx={{ color: 'text.secondary', fontSize: 14 }}>
                            {price.suffix}
                          </Box>
                        </Typography>
                      )}
                      <Typography variant="caption" sx={{ display: 'block', color: 'primary.main', fontWeight: 700 }}>
                        Free for {trialDays} days
                      </Typography>
                      {pkg.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          {pkg.description}
                        </Typography>
                      )}
                      <Stack spacing={0.5} sx={{ mt: 1.25 }}>
                        {[
                          limitLine(pkg.business_limit, 'business', 'businesses', 'Unlimited businesses'),
                          limitLine(pkg.network_limit, 'connected network', 'connected networks', 'Unlimited connected networks'),
                        ].map((line) => (
                          <Stack key={line} direction="row" spacing={0.75} alignItems="center">
                            <CheckCircleRoundedIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                            <Typography variant="body2">{line}</Typography>
                          </Stack>
                        ))}
                      </Stack>
                    </Box>
                  );
                })}
              </Box>

              <Box sx={{ maxWidth: 520, mx: 'auto' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
                  Card details
                </Typography>
                <Box
                  ref={cardHostRef}
                  sx={{
                    border: '1px solid',
                    borderColor: cardError ? 'error.main' : 'divider',
                    borderRadius: 2,
                    p: 1.5,
                    minHeight: 44,
                    bgcolor: 'background.paper',
                  }}
                />
                {cardError && (
                  <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                    {cardError}
                  </Typography>
                )}
                {(loadError || (config && !stripeKey)) && (
                  <Alert severity="warning" sx={{ mt: 1, py: 0.5 }}>
                    {loadError || 'Card payments are not available right now. Please try again later.'}
                  </Alert>
                )}
                <Stack direction="row" spacing={0.75} alignItems="flex-start" sx={{ mt: 1 }}>
                  <LockOutlinedIcon sx={{ fontSize: 15, color: 'text.secondary', mt: '2px' }} />
                  <Typography variant="caption" color="text.secondary">
                    We verify your card with a small charge that is refunded straight away.
                    {chosen && chosenPrice
                      && ` After ${trialDays} days you'll be billed ${chosenPrice.amount}${chosenPrice.suffix} for ${chosen.name}.`}
                  </Typography>
                </Stack>

                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  fullWidth
                  disabled={busy || !ready || !chosen}
                  sx={{
                    mt: 2.5, py: 1.35, borderRadius: 999,
                    textTransform: 'none', fontWeight: 800, fontSize: 15,
                    background: 'linear-gradient(135deg,#3D5AFE,#6C7BFF)',
                    boxShadow: '0 12px 26px -12px rgba(61,90,254,.75)',
                    '&:hover': { background: 'linear-gradient(135deg,#3350F0,#5A6CFF)' },
                  }}
                >
                  {busy
                    ? 'Starting your free trial…'
                    : `Start ${trialDays}-day free trial${chosen ? ` — ${chosen.name}` : ''}`}
                </Button>

                <Typography variant="body2" align="center" color="text.secondary" sx={{ mt: 2 }}>
                  Signed in as {me?.user?.email || user?.email}.{' '}
                  <Link
                    component="button"
                    type="button"
                    onClick={signOut}
                    sx={{ fontWeight: 600, verticalAlign: 'baseline' }}
                  >
                    Sign out
                  </Link>
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
